/**
 * TaskInputForm — Dynamic form generated from Phase 0 JSON Schema
 *
 * Renders a JSON Schema (draft-07) as a validated Tailwind form via RJSF.
 * Shown after Phase 0 analysis when input_form_schema is non-null.
 *
 * Layout:
 *  - Primary: RJSF form with custom Tailwind widgets + per-field customisation controls
 *  - Secondary (collapsible): TaskBrief summary (objectives, estimates)
 *  - Actions: [Approve & Continue] [Regenerate Form] [Reset to original] [Cancel]
 *
 * Field customisation (2193.3):
 *  - Remove field — × button removes from schema + required array
 *  - Rename label — pencil → inline edit of field title
 *  - Reorder — ▲▼ arrows move field up/down (updates ui:order)
 */

import React, { useState, useCallback, useMemo } from 'react';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import {
  ChevronDown,
  ChevronRight,
  Loader,
  RefreshCw,
  CheckCircle,
  X,
  AlertCircle,
  Target,
  DollarSign,
  Clock,
  Pencil,
  ChevronUp,
  RotateCcw,
} from 'lucide-react';
import { tailwindWidgets } from '../widgets';
import { useTaskBriefApproval, useTaskBriefRejection } from '../hooks/useTaskBrief';

const MAX_ITERATIONS = 3;

// ─── Schema customisation helpers ────────────────────────────────────────────

/**
 * Build initial field order from schema properties (preserving definition order).
 */
function initialFieldOrder(schema) {
  if (!schema?.properties) return [];
  return Object.keys(schema.properties);
}

/**
 * Apply customisations to the base schema:
 * - removedFields: set of field keys to exclude
 * - renamedLabels: { fieldKey: newTitle }
 * Returns a new schema object (does not mutate).
 */
function applyCustomisations(schema, removedFields, renamedLabels) {
  if (!schema?.properties) return schema;
  const newProps = {};
  for (const [key, prop] of Object.entries(schema.properties)) {
    if (removedFields.has(key)) continue;
    newProps[key] = renamedLabels[key]
      ? { ...prop, title: renamedLabels[key] }
      : prop;
  }
  const newRequired = (schema.required || []).filter((k) => !removedFields.has(k));
  return { ...schema, properties: newProps, required: newRequired };
}

/**
 * Build uiSchema: widget hints + ui:order + per-field customisation callbacks
 * passed via ui:options so the custom FieldTemplate can access them.
 */
function buildUiSchema(schema, fieldOrder, onRemove, onRenameStart, onMoveUp, onMoveDown) {
  if (!schema?.properties) return {};
  const uiSchema = { 'ui:order': [...fieldOrder, '*'] };
  const keys = fieldOrder.filter((k) => k in (schema.properties || {}));

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const prop = schema.properties[key];
    const entry = {};

    if (prop.format === 'data-url') entry['ui:widget'] = 'FileWidget';
    else if (prop.format === 'textarea') entry['ui:widget'] = 'textarea';
    else if (prop.format === 'date') entry['ui:widget'] = 'date';

    entry['ui:options'] = {
      _customise: {
        onRemove: () => onRemove(key),
        onRenameStart: () => onRenameStart(key),
        canMoveUp: i > 0,
        canMoveDown: i < keys.length - 1,
        onMoveUp: () => onMoveUp(key),
        onMoveDown: () => onMoveDown(key),
      },
    };
    uiSchema[key] = entry;
  }
  return uiSchema;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CollapsibleSection({ title, icon: Icon, children, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-blue-600" />
          <span className="text-sm font-medium text-gray-800">{title}</span>
        </div>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {expanded && <div className="p-4 bg-white text-sm">{children}</div>}
    </div>
  );
}

/**
 * Custom RJSF FieldTemplate — renders label, description, widget, errors,
 * and the customisation control bar (remove / rename / reorder).
 *
 * Customisation callbacks arrive via uiSchema ui:options._customise.
 */
function FieldTemplate({
  id, label, required, description, children, rawErrors,
  uiSchema: fieldUiSchema,
  // editingField / onConfirmRename come from context via a closure trick below
  editingField, editingValue, onEditChange, onConfirmRename,
}) {
  const hasError = rawErrors && rawErrors.length > 0;
  const customise = fieldUiSchema?.['ui:options']?._customise;

  // Extract field key from RJSF id (format: "root_fieldName")
  const fieldKey = id?.replace(/^root_/, '');
  const isEditing = editingField === fieldKey;

  return (
    <div className="mb-4 group">
      {/* Control bar */}
      {customise && (
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1 min-w-0">
            {isEditing ? (
              <input
                autoFocus
                type="text"
                value={editingValue}
                onChange={(e) => onEditChange(e.target.value)}
                onBlur={() => onConfirmRename(fieldKey)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onConfirmRename(fieldKey);
                  if (e.key === 'Escape') onConfirmRename(null);
                }}
                className="text-sm font-medium text-gray-700 border-b border-blue-400 focus:outline-none bg-transparent w-full max-w-xs"
              />
            ) : (
              <span className="text-sm font-medium text-gray-700 truncate">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
              </span>
            )}
            {!isEditing && (
              <button
                type="button"
                title="Rename field"
                onClick={customise.onRenameStart}
                className="ml-1 p-0.5 text-gray-300 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Pencil size={12} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-0.5 shrink-0 ml-2">
            <button
              type="button"
              title="Move up"
              disabled={!customise.canMoveUp}
              onClick={customise.onMoveUp}
              className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-20 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronUp size={14} />
            </button>
            <button
              type="button"
              title="Move down"
              disabled={!customise.canMoveDown}
              onClick={customise.onMoveDown}
              className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-20 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronDown size={14} />
            </button>
            <button
              type="button"
              title="Remove field"
              onClick={customise.onRemove}
              className="p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Label (shown only when no customise controls, e.g. root object) */}
      {!customise && label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      {description && (
        <p className="text-xs text-gray-500 mb-1.5">{description}</p>
      )}
      {children}
      {hasError && (
        <div className="mt-1 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle size={12} />
          <span>{rawErrors[0]}</span>
        </div>
      )}
    </div>
  );
}

function ObjectFieldTemplate({ properties }) {
  return <div className="space-y-0">{properties.map((p) => p.content)}</div>;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function TaskInputForm({ taskBriefData, onApprove, onCancel, onApproveOverride }) {
  const brief = taskBriefData?.iteration?.brief;
  // Brief data arrives snake_case from the reject/modify endpoints but
  // camelCase from useAIWorkflowDecompose's Phase 0 mapping — accept both
  // (brainstorm-z8t2: "briefId is required" on approve from starter cards).
  const briefId = taskBriefData?.task_brief_id || taskBriefData?.taskBriefId;
  const iteration = taskBriefData?.current_iteration || taskBriefData?.currentIteration || 1;
  const originalSchema = brief?.input_form_schema;

  // ── customisation state
  const [removedFields, setRemovedFields] = useState(() => new Set());
  const [renamedLabels, setRenamedLabels] = useState({});
  const [fieldOrder, setFieldOrder] = useState(() => initialFieldOrder(originalSchema));
  const [editingField, setEditingField] = useState(null);   // key being renamed
  const [editingValue, setEditingValue] = useState('');

  // ── form state
  const [formData, setFormData] = useState({});
  const [regenFeedback, setRegenFeedback] = useState('');
  const [showRegenInput, setShowRegenInput] = useState(false);
  const [localError, setLocalError] = useState(null);

  const { approve, approving, error: approveError } = useTaskBriefApproval(briefId);
  const { reject, rejecting, error: rejectError } = useTaskBriefRejection(briefId);

  // ── derived schema (memoised)
  const customisedSchema = useMemo(
    () => applyCustomisations(originalSchema, removedFields, renamedLabels),
    [originalSchema, removedFields, renamedLabels]
  );

  // ── customisation handlers
  const handleRemove = useCallback((key) => {
    setRemovedFields((prev) => new Set([...prev, key]));
    setFieldOrder((prev) => prev.filter((k) => k !== key));
    setFormData((prev) => { const n = { ...prev }; delete n[key]; return n; });
  }, []);

  const handleRenameStart = useCallback((key) => {
    const currentTitle = renamedLabels[key] || originalSchema?.properties?.[key]?.title || key;
    setEditingField(key);
    setEditingValue(currentTitle);
  }, [renamedLabels, originalSchema]);

  const handleEditChange = useCallback((val) => setEditingValue(val), []);

  const handleConfirmRename = useCallback((key) => {
    if (key && editingValue.trim()) {
      setRenamedLabels((prev) => ({ ...prev, [key]: editingValue.trim() }));
    }
    setEditingField(null);
    setEditingValue('');
  }, [editingValue]);

  const handleMoveUp = useCallback((key) => {
    setFieldOrder((prev) => {
      const idx = prev.indexOf(key);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  }, []);

  const handleMoveDown = useCallback((key) => {
    setFieldOrder((prev) => {
      const idx = prev.indexOf(key);
      if (idx === -1 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setRemovedFields(new Set());
    setRenamedLabels({});
    setFieldOrder(initialFieldOrder(originalSchema));
    setEditingField(null);
  }, [originalSchema]);

  const isCustomised = removedFields.size > 0 || Object.keys(renamedLabels).length > 0
    || JSON.stringify(fieldOrder) !== JSON.stringify(initialFieldOrder(originalSchema));

  // ── uiSchema (memoised, rebuilt when order/callbacks change)
  const uiSchema = useMemo(
    () => buildUiSchema(
      customisedSchema,
      fieldOrder.filter((k) => !removedFields.has(k)),
      handleRemove, handleRenameStart, handleMoveUp, handleMoveDown
    ),
    [customisedSchema, fieldOrder, removedFields, handleRemove, handleRenameStart, handleMoveUp, handleMoveDown]
  );

  // ── FieldTemplate with customisation callbacks injected via closure
  const BoundFieldTemplate = useCallback((props) => (
    <FieldTemplate
      {...props}
      editingField={editingField}
      editingValue={editingValue}
      onEditChange={handleEditChange}
      onConfirmRename={handleConfirmRename}
    />
  ), [editingField, editingValue, handleEditChange, handleConfirmRename]);

  // ── approve / regenerate
  const handleApprove = useCallback(async () => {
    setLocalError(null);
    // onApproveOverride: used when there is no task brief (e.g. template with stored schema).
    // Skips the brief-approval endpoint and passes form data directly to the caller.
    if (onApproveOverride) {
      onApproveOverride(formData, customisedSchema);
      return;
    }
    const result = await approve(true, formData, customisedSchema);
    if (result) onApprove(result);
  }, [approve, formData, customisedSchema, onApprove, onApproveOverride]);

  const handleRegenerate = useCallback(async () => {
    setLocalError(null);
    const feedback = regenFeedback.trim() || 'Please regenerate the form with better fields for this task.';
    const result = await reject(feedback);
    if (result) onApprove({ _regenerated: true, ...result });
  }, [reject, regenFeedback, onApprove]);

  const canRegenerate = iteration < MAX_ITERATIONS;
  const busy = approving || rejecting;
  const errorMsg = localError || approveError?.message || rejectError?.message;

  if (!originalSchema) return null;

  const visibleFieldCount = fieldOrder.filter((k) => !removedFields.has(k)).length;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Before we start, a few details</h2>
          <p className="text-sm text-gray-500">
            Fill in the fields below. Use the controls beside each label to rename, reorder, or remove a field.
            {iteration > 1 && (
              <span className="ml-2 text-blue-600 font-medium">Iteration {iteration}/{MAX_ITERATIONS}</span>
            )}
          </p>
        </div>
        {isCustomised && (
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 shrink-0 ml-4"
          >
            <RotateCcw size={12} />
            Reset to original
          </button>
        )}
      </div>

      {/* Empty state */}
      {visibleFieldCount === 0 && (
        <div className="py-8 text-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg">
          All fields removed.{' '}
          <button type="button" onClick={handleReset} className="text-blue-500 hover:underline">
            Reset to original
          </button>
        </div>
      )}

      {/* RJSF Form */}
      {visibleFieldCount > 0 && (
        <Form
          schema={customisedSchema}
          uiSchema={uiSchema}
          formData={formData}
          widgets={tailwindWidgets}
          templates={{ FieldTemplate: BoundFieldTemplate, ObjectFieldTemplate }}
          validator={validator}
          onChange={({ formData: fd }) => setFormData(fd || {})}
          onSubmit={handleApprove}
          children={<span />}
          liveValidate={false}
          noHtml5Validate
        />
      )}

      {/* Brief summary — collapsible */}
      {brief && (
        <CollapsibleSection title="What the system understood" icon={Target}>
          <div className="space-y-3">
            {brief.task_summary && (
              <p className="text-gray-700">{brief.task_summary}</p>
            )}
            {brief.objectives?.length > 0 && (
              <div>
                <p className="font-medium text-gray-800 mb-1">Objectives</p>
                <ul className="list-disc list-inside space-y-0.5 text-gray-600">
                  {brief.objectives.map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              </div>
            )}
            {brief.estimates && (
              <div className="flex gap-4 text-gray-600 pt-1 border-t border-gray-100">
                {brief.estimates.estimated_cost_usd != null && (
                  <span className="flex items-center gap-1">
                    <DollarSign size={13} />
                    Est. cost: ${brief.estimates.estimated_cost_usd.toFixed(3)}
                  </span>
                )}
                {brief.estimates.estimated_duration_minutes != null && (
                  <span className="flex items-center gap-1">
                    <Clock size={13} />
                    Est. time: {brief.estimates.estimated_duration_minutes}m
                  </span>
                )}
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Regenerate feedback input */}
      {showRegenInput && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            What should change about the form?
          </label>
          <textarea
            rows={2}
            value={regenFeedback}
            onChange={(e) => setRegenFeedback(e.target.value)}
            placeholder="e.g. Add a field for the reporting currency, remove the date field"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {/* Error */}
      {errorMsg && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={14} className="shrink-0" />
          {errorMsg}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
        <button
          type="button"
          disabled={busy}
          onClick={handleApprove}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
        >
          {approving ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
          {approving ? 'Processing…' : 'Approve & Continue'}
        </button>

        {canRegenerate && !showRegenInput && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setShowRegenInput(true)}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
          >
            <RefreshCw size={14} />
            Regenerate Form
          </button>
        )}

        {showRegenInput && (
          <button
            type="button"
            disabled={busy}
            onClick={handleRegenerate}
            className="flex items-center gap-2 px-4 py-2.5 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50 text-sm"
          >
            {rejecting ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {rejecting ? 'Regenerating…' : 'Submit & Regenerate'}
          </button>
        )}

        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="flex items-center gap-1.5 px-4 py-2.5 text-gray-500 hover:text-gray-700 disabled:opacity-50 text-sm ml-auto"
        >
          <X size={14} />
          Cancel
        </button>
      </div>
    </div>
  );
}
